import React, { Suspense, useMemo, useState } from "react";
import lazy from '@/chunk-loader';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import { GetDefaultMainNavItems } from "../../../../store/utils/create-event";
import { useScreenMediaQuery } from '../../../../utils/use-screen-media-query';
import { Channel, LanguagesAbbr, EventNavbar, ENavbarTypes, NavMainItems } from "../../../../types/working-model";
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { workingEventDefaultLanguage } from "../../../admin/utils/utils";
import { getEventNavbarStyling } from "../../utils";
import Icon, { COLORS } from '../../../general-ui/icon';
import { OptionalComponent } from '../../../../utils/optional-component';
import { useIsSessionPage, useIsSingleSessionPage } from '../../../../hooks/path.hooks';
import { MarketingNavMenu } from '../navigation/marketing-nav';
import EventLogo from '../navigation/event-logo';
import { useGetNavIconName } from './hooks/navigation-icon.hooks';
import NavigationToggle from './navigation-toggle';
import { EIcon } from '../../../general-ui/icon/icon';
import { useGetEvent, useIsAboveTheFold, useIsSingleSession, useIsSingleSessionWithoutHome } from '../../../../hooks/session.hooks';
import { toggleNavigation } from '../../../../store/actions/admin/content-actions';
import { useIsNewNavigation } from '../../../../hooks/navigation.hooks';
import { useVideoV2 } from "../../../../hooks/channel.hooks";

import './marketing-nav-v2.scss';
import { useCheckShowNavigation } from "./hooks/navigation.hooks";
import { useGetSession } from "../../../../hooks/session.hooks";
const PostRegisterSideBar = lazy(() => import("../../../admin/create/homepage/main-preview-components/post-register-side-bar"));
const EditEventHeaderModal = lazy(() => import("../../../admin/create/session/editor/edit-event-header-modal/edit-event-header-modal"));

interface MarketingNavV2Props {
	preview?: boolean;
	currentChannel?: Channel;
	template: string;
	previewHide?: boolean;
	logo?: string;
	previewLanguage?: LanguagesAbbr
	showLanguageSelector?: boolean;
	disableProfileBubble?: boolean;
	hideButtons?: boolean;
	bannerAnnouncementShowing?: boolean;
	themeLogo?: string;
}

/** @deprecated MarketingNavV2 is deprecated. Look at navbar.tsx instead. */
export default function MarketingNavV2({
	preview,
	currentChannel,
	template,
	previewHide,
	logo,
	previewLanguage,
	showLanguageSelector = false,
	disableProfileBubble = false,
	hideButtons,
	bannerAnnouncementShowing,
	themeLogo,
}: MarketingNavV2Props): JSX.Element | null {
	const dispatch = useDispatch();
	const previewMode = useTypedSelector(state => state.CreateEventReducer.previewMode);
	const isNavigationOpen = useTypedSelector(state => state.ContentReducer.isNavigationOpen);
	const [openEditModal, setOpenEditModal] = useState<boolean>(false);

	useCheckShowNavigation();
	const isDesktop = !useScreenMediaQuery().isLessThan1024;
	const isSessionPage = useIsSessionPage();
	const isSingleSession = useIsSingleSession();
	const isSingleSessionPage = useIsSingleSessionPage();
	const isSingleSessionWithoutHomePage = useIsSingleSessionWithoutHome();
	const isNavigationV2 = useIsNewNavigation();
	const event = useGetEvent();
	const videoV2 = useVideoV2();
	const session = useGetSession();
	const isSessionDetailsAboveTheFoldV2 = useIsAboveTheFold(session);

	// If caller didn't explicitly set hideButtons, check if videoV2 is enabled and disallow menu
	// The videoV2 component itself will show a menu.
	const hideNavigationToggle = (hideButtons === undefined && videoV2)
		? true
		: !!hideButtons;

	const { backgroundColor, backgroundImg } = useMemo(() => {
		const workingNavbar: EventNavbar | undefined = event?.settings.event_navbar;

		return getEventNavbarStyling(workingNavbar?.styling_overrides);
	}, [event?.settings.event_navbar]);

	const navbarType = event?.settings?.event_navbar?.navbarType;
	const navIconName = useGetNavIconName({ navbarType });

	const isSticky = event?.settings?.event_navbar?.isSticky;
	const blurLevel = event?.settings?.event_navbar?.blurLevel;
	const isTopNavbar = navbarType === ENavbarTypes.Horizontal;

	const checkSessionPage = isSessionPage || (isSessionPage && isSingleSession) || isSingleSessionPage;
	const shouldShowNewNav = isNavigationV2 && isDesktop;
	const isSessionPageNewNav = isNavigationV2 && isDesktop && checkSessionPage;
	const customPages = event?.custom_pages?.filter(page => page.is_on && !page.deleted);

	const headerStyle = {
		...backgroundImg,
		'--blur-level': blurLevel || 0,
	} as unknown as React.CSSProperties;

	if (previewHide && !isNavigationV2) return null;

	// no CSS in place to handle horizontal top nav OR vertical side nav based on event settings
	// for single session events, causing a broken experience when the flag is on. Do not re-enable
	// without thorough testing both with new and old events, carefully checking single-session with and without
	// homepage as well as all other iterations, and check both in the editor AND on the live site.
	// if (isSingleSessionWithoutHomePage) return null;

	return (
		<>
			{!hideNavigationToggle && (
				<NavigationToggle isDesktop={isSessionPageNewNav} hideToggle={isNavigationOpen} bannerAnnouncementShowing={bannerAnnouncementShowing} />
			)}

			<header
				className={classNames(
					'marketing-page-navigation',
					template,
					backgroundColor,
					{
						'preview-mode': previewMode,
						'show-navigation': isSessionPageNewNav ? isNavigationOpen : true,
						'navigation-fixed': isSessionPageNewNav ? isNavigationOpen : true,
						'navigation-toggle-active': isSessionPageNewNav || true,
						'new-navigation': shouldShowNewNav,
						'not-session-page': isNavigationV2 && !isSessionPageNewNav,
						'new-top-navigation': (isTopNavbar && shouldShowNewNav) || isSingleSessionWithoutHomePage,
						'sticky-top-navigation': isSticky && shouldShowNewNav,
						'shorten-for-banner-announcement': event?.announcements && event.announcements.length > 0 && isSessionDetailsAboveTheFoldV2 && isSessionPage
					})}
				style={headerStyle}
			>

				{event && preview && (
					<Suspense fallback="">
						<PostRegisterSideBar
							template={template}
							eventMainNavItems={event.homepage?.event_main_nav_items || GetDefaultMainNavItems(workingEventDefaultLanguage(event))}
							activeMenuItem={isSingleSession ? NavMainItems.Session : NavMainItems.Sessions}
							logo={event.homepage?.header.logo}
							disableProfileBubble={true}
							customPages={customPages}
						/>
					</Suspense>
				)}

				{!preview && (
					<>
						<div className="navbar-left">
							<OptionalComponent display={isSessionPageNewNav}>
								<span className={classNames('navigation-fixed-button')} onClick={() => dispatch(toggleNavigation(!isNavigationOpen))}>
									{isNavigationOpen ? (
										<Icon name={navIconName} color={COLORS.BLACK} size={20} />
									) : (
										<EIcon name='menu' size='large' className='icon-menu' />
									)}
								</span>
							</OptionalComponent>
							<EventLogo logo={logo} themeLogo={themeLogo} channel={currentChannel} />
						</div>
						<nav className={classNames("navigation", { "nav-items": isDesktop })}>
							<MarketingNavMenu
								disableProfileBubble={disableProfileBubble}
								showLanguageSelector={showLanguageSelector}
								previewLanguage={previewLanguage}
								template={template}
							/>
						</nav>
					</>
				)}
			</header>

			<Suspense fallback="">
				<EditEventHeaderModal open={openEditModal} closeModal={() => setOpenEditModal(false)} />
			</Suspense>
		</>
	);
}
